import { Person, Receipt } from '@mui/icons-material';
import {
  Box,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Avatar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GETBookingById, UpdateBookingStatus } from '../../services/booking';
import { IBooking } from '../../utils/entities/interface';
import { BookingStatus } from '../../utils/entities/type';
import useSnackbar from '../../hooks/useSnackbar';
import { formatDate } from '../../utils/helper';
import { isGstAppliedOnBooking } from '../../utils/invoiceGst';
import { AddOrderItems } from '../../components/common/AddOrderItem';
import {
  canManageBookingInvoice,
  getInvoiceActionLabel,
  hasBookingInvoice,
} from '../../utils/bookingInvoice';
import { DetailPageHeader } from '../../components/layout/DetailPageHeader';
import { APP_SHELL_HEADER_CONTROL_HEIGHT } from '../../components/layout/layoutConstants';

export const PaymentDetails = () => {
  const { paymentId } = useParams();
  const [payment, setPayment] = useState<IBooking | null>(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    if (paymentId) {
      fetchPaymentDetails(paymentId);
    }
  }, [paymentId]);

  const fetchPaymentDetails = async (id: string) => {
    try {
      const result = await GETBookingById(id);

      setPayment(result?.data || null);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setPayment(null);
    }
  };

  const reloadPayment = () => {
    if (paymentId) {
      fetchPaymentDetails(paymentId);
    }
  };

  const hasInvoice = hasBookingInvoice(payment);
  const canManageInvoice = canManageBookingInvoice(payment?.status, hasInvoice);

  const handleStatusChange = (status: BookingStatus) => {
    if (!paymentId) return;

    UpdateBookingStatus(paymentId, status)
      .then((responseStatus: number) => {
        if (responseStatus === 200) {
          showSnackbar('Amount Collected', 'success');

          setTimeout(() => {
            navigate(-1);
          }, 500);
        }
      })
      .catch((error: Error) => {
        showSnackbar(error.message, 'error');
        console.error(
          `Error updating Booking status to ${status}:`,
          error.message,
        );
      });
  };

  const customerName = payment?.user?.name || 'N/A';
  const customerPhone = `${payment?.user?.countryCode || ''} ${
    payment?.user?.phoneNumber || 'N/A'
  }`;

  const address = payment?.addressDetails?.[0];

  const fullAddress = address
    ? `${address?.house || ''}, ${address?.street || ''}, ${
        address?.city || ''
      }, ${address?.state || ''}, ${address?.zipcode || ''}`
    : 'N/A';

  const landmark = address?.landmark || 'N/A';

  const technicianName = payment?.technician?.name || 'N/A';
  const technicianPhoto = payment?.technician?.profilePhoto || '';
  const technicianPhone = `${payment?.technician?.countryCode || ''} ${
    payment?.technician?.phoneNumber || 'N/A'
  }`;

  const orderItems = payment?.orderItems || [];
  const gstApplied = isGstAppliedOnBooking(payment);
  const gstRate = gstApplied ? Number(payment?.tax?.gst || 0) : 0;
  const grandTotal = Number(payment?.grandTotal || 0);
  return (
    <Box p={1} bgcolor="#121212" color="#ffffff">
      <AddOrderItems
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        orderItems={hasInvoice ? payment!.orderItems : []}
        booking={payment}
        onReload={reloadPayment}
      />
      <DetailPageHeader
        title="Payment Details"
        actions={
          <>
            {canManageInvoice && (
              <Button
                size="small"
                variant="contained"
                startIcon={<Receipt />}
                onClick={() => setInvoiceModalOpen(true)}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#407BFF',
                  height: APP_SHELL_HEADER_CONTROL_HEIGHT,
                  '&:hover': { backgroundColor: '#3366d6' },
                }}
              >
                {getInvoiceActionLabel(hasInvoice)}
              </Button>
            )}
            <Chip
              label={payment?.status || 'PENDING'}
              color="warning"
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 'bold',
                color: '#ffa726',
                height: APP_SHELL_HEADER_CONTROL_HEIGHT,
              }}
            />
          </>
        }
      />

      {SnackbarComponent}

      <Grid container spacing={3} padding={2}>
        {/* Customer Details */}
        <Grid item md={4} sm={12} xs={12}>
          <Box
            sx={{
              borderRadius: 2,
              height: '100%',
              backgroundColor: '#1e1e1e',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
            }}
          >
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {customerName !== 'N/A' ? customerName.charAt(0) : 'N'}
                </Avatar>
              }
              title="Customer Details"
              subheader={customerName}
              subheaderTypographyProps={{ color: '#aaaaaa' }}
            />

            <Box padding={2}>
              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Phone:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {customerPhone}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Address:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {fullAddress}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex' }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Landmark:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {landmark}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Technician Details */}
        <Grid item md={4} sm={12} xs={12}>
          <Box
            sx={{
              borderRadius: 2,
              height: '100%',
              backgroundColor: '#1e1e1e',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
            }}
          >
            <CardHeader
              avatar={
                <Avatar src={technicianPhoto} alt={technicianName}>
                  {technicianName !== 'N/A' ? technicianName.charAt(0) : 'T'}
                </Avatar>
              }
              title="Technician Details"
              subheader={technicianName}
              subheaderTypographyProps={{ color: '#aaaaaa' }}
            />

            <Box padding={2}>
              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Phone:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {technicianPhone}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Position:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {payment?.technician?.position || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex' }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Status:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {payment?.technician?.status?.replace('_', ' ') || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Booking Details */}
        <Grid item md={4} sm={12} xs={12}>
          <Box
            sx={{
              borderRadius: 2,
              height: '100%',
              backgroundColor: '#1e1e1e',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
            }}
          >
            <CardHeader
              title="Booking Details"
              subheaderTypographyProps={{ color: '#aaaaaa' }}
            />

            <Box padding={2}>
              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Booking Date:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {payment?.date ? formatDate(payment.date) : 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Booking ID:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {payment?.bookingId || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex' }}>
                <Typography variant="body1" sx={{ color: '#BDBDBD', flex: 1 }}>
                  <strong>Time Slot:</strong>
                </Typography>
                <Typography variant="body1" sx={{ flex: 2 }}>
                  {payment?.slot?.replace('_', ' ') || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Order Items Summary */}
        <Grid item xs={12}>
          <Box
            sx={{
              borderRadius: 2,
              backgroundColor: '#1e1e1e',
              border: '2px solid #f44336',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
            }}
          >
            <CardContent>
              <Box
                padding={2}
                sx={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ color: '#f44336', flex: 1 }}
                  >
                    <strong>Bill TO:</strong>
                  </Typography>

                  <Typography variant="body1" sx={{ flex: 2 }}>
                    {customerName}
                  </Typography>

                  <Box sx={{ display: 'flex' }}>
                    <Typography
                      variant="body1"
                      sx={{ color: '#BDBDBD', flex: 1 }}
                    >
                      <strong>Phone:</strong>
                    </Typography>

                    <Typography variant="body1">{customerPhone}</Typography>
                  </Box>
                </Box>

                <Box sx={{ alignItems: 'flex-end' }}>
                  <Typography
                    variant="body1"
                    sx={{ color: '#f44336', flex: 1, textAlign: 'right' }}
                  >
                    <strong>Invoice Details</strong>
                  </Typography>

                  <Typography variant="body1" sx={{ textAlign: 'right' }}>
                    Invoice ID: {payment?.invoiceId || 'N/A'}
                  </Typography>

                  <Typography variant="body1" sx={{ textAlign: 'right' }}>
                    Date: {payment?.date ? formatDate(payment.date) : 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead sx={{ background: '#222' }}>
                    <TableRow>
                      <TableCell>
                        <strong>Item</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Quantity</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Price</strong>
                      </TableCell>
                      {gstApplied ? (
                        <TableCell align="right">
                          <strong>GST ({gstRate}%)</strong>
                        </TableCell>
                      ) : null}
                      <TableCell align="right">
                        <strong>Total</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {orderItems.length > 0 ? (
                      orderItems.map((item: any) => {
                        const quantity = Number(item?.quantity || 0);
                        const price = Number(item?.price || 0);

                        const subtotal = price * quantity;
                        const gstAmount = gstApplied
                          ? (subtotal * gstRate) / 100
                          : 0;
                        const total = subtotal + gstAmount;

                        return (
                          <TableRow key={item?._id || item?.item}>
                            <TableCell>{item?.item || 'N/A'}</TableCell>

                            <TableCell align="center">{quantity}</TableCell>

                            <TableCell align="right">₹{price}</TableCell>

                            {gstApplied ? (
                              <TableCell align="right">
                                ₹{gstAmount.toFixed(2)}
                              </TableCell>
                            ) : null}

                            <TableCell align="right">
                              ₹{total.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={gstApplied ? 5 : 4} align="center">
                          No order items found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  textAlign: 'right',
                }}
              >
                <Typography variant="h6" align="right">
                  <strong>Total Amount:</strong> ₹{grandTotal.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Box>

          <Box display="flex" justifyContent="flex-end" marginY={3}>
            <Button
              sx={{
                color: '#fff',
                backgroundColor: '#0C9005',
                padding: 1,
                borderRadius: 2,
                marginLeft: 2,
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  backgroundColor: '#087504',
                },
              }}
              startIcon={<Person />}
              onClick={() => handleStatusChange('PAID')}
            >
              Paid
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
